/* eslint-disable no-unused-vars */
import React, { memo, useState, useEffect, useMemo, forwardRef } from 'react';
import _ from 'lodash';
import { store } from 'redux/store';

import SearchInput from '../search-input';
import NewTabs from '@components/new-tabs';
import StructureListingInner from '@components/structure-listing-inner';
import SiteListingInner from '@components/site-listing-inner';
import EquipmentListingInner from '@components/equipment-listing/equipment-listing-inner';
import VirtualList from '@components/virtual-list/virtual-list';
import LoadingRound from '@components/loading-round/loading-round';
import PipelineListingInner from '@components/pipeline-listing/pipeline-listing-inner/pipeline-listing-inner.component';

import { useDebounce } from '@hooks/useDebounce';
import { filterEquipments, filterSites, filterStructure, filterPipeline } from './helper';
import { initWebWorker } from 'webworker';

import './search-tab-list.scss';
import PullToRefresh from '@components/pull-to-refresh';
import { distanceFilter } from '@components/site-listing/helper';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { checkIfTheLocationIsNeedToUpdateOrNot } from '@utilities/date-filters';
import Checkbox from '@components/checkbox/checkbox.component';

const components = {
  site: SiteListingInner,
  equipment: EquipmentListingInner,
  structure: StructureListingInner,
  pipeline: PipelineListingInner
};

const SearchList = (
  {
    label = '',
    onSelect,
    setIsLoadingOnParent,
    equipmentCategories = [],
    psvEquipments = false,
    alreadyAttachedPSV = [],
    siteId = null
  },
  ref
) => {
  const [active, setActive] = useState('0');
  const key = label.toLowerCase();
  const Component = components[key];
  const [inputValue, setInputValue] = useState('');
  const [sites, setSites] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [structures, setStructures] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPSVOnOtherSites, setShowPSVOnOtherSite] = useState(false);
  const { sitesMap = {}, sites: cacheSites = [] } = useSelector((state) => state.cache);
  const { coordinates, unit, locationUpdateTime } = useSelector((state) => state.cache);
  const [searchParams] = useSearchParams();
  const refetchSitesLocation = searchParams.get('RefetchSitesLocation');

  const debouncedSearchInputValue = useDebounce(inputValue, 1000);

  useEffect(() => {
    const type =
      key === 'site'
        ? 'get-all-site-listings'
        : key === 'equipment'
        ? 'get-all-equipment-listing'
        : key === 'pipeline'
        ? 'get-all-pipeline-listing'
        : 'get-all-structure-listing';

    initWebWorker({
      type,
      args: {
        store: { ...store.getState() },
        equipmentCategories,
        psvEquipments,
        alreadyAttachedPSV,
        createSiteDataPoints: key === 'equipment' ? true : false
      }
    })
      .then((data) => {
        key === 'site'
          ? setSites(data?.result)
          : key === 'equipment'
          ? setEquipments(data?.result)
          : key === 'pipeline'
          ? setPipelines(data?.result)
          : setStructures(data?.result);
        setInitialLoading(false);
      })
      .catch((error) => {
        setInitialLoading(false);
      });
  }, [key]);

  useEffect(() => {
    if (!initialLoading) {
      filterAndSortedData();
    }
  }, [debouncedSearchInputValue, initialLoading]);

  useEffect(() => {
    if (refetchSitesLocation) {
      checkIfTheLocationIsNeedToUpdateOrNot({
        addNewCoordinates: true,
        isOnline: navigator.onLine,
        unit,
        locationUpdateTime,
        oldCoordinates: coordinates,
        functionCall: filterAndSortedData,
        loadingFunctionCall: () => {
          // setIsLoading(true);
        },
        params: {
          coordinates
        }
      });
    }
  }, [refetchSitesLocation, navigator.onLine]);

  const filterAndSortedData = async () => {
    try {
      setIsLoading(true);
      if (key === 'site' && _.size(sites) > 0) {
        let filterSitesData = await filterSites({
          sites,
          inputValue: debouncedSearchInputValue
        });
        filterSitesData = filterSitesData.sort((a, b) => {
          return distanceFilter(a, b);
        });
        setFilterData([...filterSitesData]);
      } else if (key === 'equipment' && _.size(equipments) > 0) {
        let filterEquipmentData = await filterEquipments({
          equipments,
          inputValue: debouncedSearchInputValue,
          store: { ...store.getState() }
        });
        filterEquipmentData = filterEquipmentData.sort((a, b) => {
          return equipmentDistanceFilter(a, b, sitesMap, cacheSites);
        });
        setFilterData([...filterEquipmentData]);
      } else if (key === 'structure' && _.size(structures) > 0) {
        let filterStructureList = await filterStructure({
          structures,
          inputValue: debouncedSearchInputValue,
          store: { ...store.getState() }
        });
        filterStructureList = filterStructureList.sort((a, b) => {
          return structureDistanceFilter(a, b, sitesMap, cacheSites);
        });

        setFilterData([...filterStructureList]);
      } else if (key === 'pipeline' && _.size(pipelines) > 0) {
        let filterPipelineList = await filterPipeline({
          pipelines,
          inputValue: debouncedSearchInputValue,
          store: { ...store.getState() }
        });
        filterPipelineList = filterPipelineList.sort((a, b) => {
          return pipelineSiteDueDateSort(a, b, sitesMap, cacheSites);
        });

        setFilterData([...filterPipelineList]);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1);
    }
  };

  const filtered = useMemo(() => {
    if (psvEquipments && !showPSVOnOtherSites && siteId)
      return filterData?.filter((x) => x?.siteId === siteId) || [];
    return filterData;
  }, [filterData, showPSVOnOtherSites, siteId, psvEquipments]);

  if (!Component) return '';

  const items = [
    {
      label: `${label} (${filtered.length.toLocaleString()})`,
      children: (
        <PullToRefresh noPullToDownRefresh={false}>
          <div className={`scroll-list-content  ${psvEquipments && 'scroll-list-content-psv'}`}>
            <VirtualList
              divider
              data={filtered}
              component={Component}
              componentProps={{
                onSelect,
                highlight: inputValue,
                highlightName: true,
                addNewWizard: true,
                psvEquipments: true
              }}
            />
          </div>
        </PullToRefresh>
      )
    }
  ];

  useEffect(() => {
    const parent = document.querySelector('.wizard-steps--content');
    const parent1 = document.querySelector('.wizard-steps');
    const parent2 = document.querySelector('.wizard-steps--title');

    parent.style.overflow = isLoading ? 'hidden' : '';
    parent1.style.backgroundColor = isLoading ? '#ffffff' : '';
    parent2.style.backgroundColor = isLoading ? '#ffffff' : '';
  }, [isLoading]);

  return (
    <>
      <div className="search-tab-list">
        <SearchInput value={inputValue} onChange={setInputValue} />

        {isLoading ? (
          <div style={{ backgroundColor: '#ffffff', width: '100%' }}>
            <LoadingRound />
          </div>
        ) : (
          <>
            {psvEquipments && (
              <div className={'psv-site-search-container'}>
                <Checkbox
                  text={'Show PSVs on other sites'}
                  checked={showPSVOnOtherSites}
                  handleChange={(e) => {
                    setShowPSVOnOtherSite(e.target.checked);
                  }}
                  textTransformNone
                  labelClassName={'psv-site-search-container-content'}
                  checkMarkAfter={'psv-site-search-container-checkbox'}
                />
              </div>
            )}
            <div
              className={`scroll-list with-search ${
                psvEquipments && 'scroll-list-with-search-psv'
              }`}
            >
              <NewTabs
                active={active}
                tabs={items}
                setActive={(tab) => setActive(tab)}
                white
                leftMenu
              >
                {items[active]?.children}
              </NewTabs>
            </div>
          </>
        )}
      </div>
      {/* )} */}
    </>
  );
};

export default memo(forwardRef(SearchList));

const pipelineSiteDueDateSort = (a, b, sitesMap, cacheSites) => {
  return (
    (cacheSites[sitesMap[a?.siteIds[0]]]?.distance != null
      ? cacheSites[sitesMap[a?.siteIds[0]]]?.distance
      : Infinity) -
      (cacheSites[sitesMap[b?.siteIds[0]]]?.distance != null
        ? cacheSites[sitesMap[b?.siteIds[0]]]?.distance
        : Infinity) || a?.name?.localeCompare(b.name)
  );
};

const equipmentDistanceFilter = (a, b, sitesMap, cacheSites) => {
  return (
    (cacheSites[sitesMap[a?.siteId]]?.distance != null
      ? cacheSites[sitesMap[a?.siteId]]?.distance
      : Infinity) -
      (cacheSites[sitesMap[b?.siteId]]?.distance != null
        ? cacheSites[sitesMap[b?.siteId]]?.distance
        : Infinity) || a?.name?.localeCompare(b.name)
  );
};

const structureDistanceFilter = (a, b, sitesMap, cacheSites) => {
  return (
    (cacheSites[sitesMap[a?.siteId]]?.distance != null
      ? cacheSites[sitesMap[a?.siteId]]?.distance
      : Infinity) -
      (cacheSites[sitesMap[b?.siteId]]?.distance != null
        ? cacheSites[sitesMap[b?.siteId]]?.distance
        : Infinity) || a?.name?.localeCompare(b.name)
  );
};
